/**
 * #856: a new deployed build is picked up without costing the owner work.
 *
 * Automatic reload happens only when the page is truly idle: nothing typed or
 * staged anywhere, no request being sent, no dialog, dictation or recent input.
 * Anything short of that defers the countdown - never the other way round.
 *
 * Owner-held state that a surface knows how to rebuild (the Main Command
 * draft, recipients and reply context) is saved to this tab's sessionStorage
 * by a registered preserver just before the reload and read back once on the
 * next load. It still blocks the automatic reload; it only lets "Reload now"
 * go ahead without asking. A surface whose state cannot be rebuilt (Notepad
 * changes not yet saved) reports itself and "Reload now" waits for it or asks.
 */
import { useEffect, useRef } from 'react';

export const UPDATE_COUNTDOWN_S = 30;
export const UPDATE_POSTPONE_MS = 30 * 60_000;
/** Owner input this recent means the page is in use, whatever else is true. */
export const UPDATE_QUIET_MS = 5_000;
/** A reload that did not land the new build within this window does not auto-reload again. */
export const UPDATE_LOOP_WINDOW_MS = 10 * 60_000;
const ATTEMPT_KEY = 'brunel.update.reloadAttempt';

export interface ReloadBlock {
  readonly reason: string;
  /** True when a registered preserver carries this state across the reload. */
  readonly preserved: boolean;
}

type Check = () => ReloadBlock | null;
type Preserver = () => boolean | void;
const checks = new Set<Check>();
const preservers = new Set<Preserver>();
const changedControls = new Set<Element>();
let mutationsInFlight = 0;

/** This tab's own store: it survives a reload and nothing else. */
export function sessionStore(): Storage | null {
  try { return globalThis.sessionStorage ?? null; } catch { return null; }
}

export function registerReloadCheck(check: Check): () => void {
  checks.add(check);
  return () => { checks.delete(check); };
}

export function registerReloadPreserver(save: Preserver): () => void {
  preservers.add(save);
  return () => { preservers.delete(save); };
}

/** Reports what would be lost; read every tick, so it may read refs. */
export function useReloadCheck(check: Check): void {
  const latest = useRef(check);
  latest.current = check;
  useEffect(() => registerReloadCheck(() => latest.current()), []);
}

export function useReloadPreserver(save: Preserver): void {
  const latest = useRef(save);
  latest.current = save;
  useEffect(() => registerReloadPreserver(() => latest.current()), []);
}

export function runReloadPreservers(): boolean {
  let saved = true;
  for (const save of preservers) {
    try { if (save() === false) saved = false; } catch { saved = false; }
  }
  return saved;
}

/** Counts non-read requests so a reload never lands in the middle of one. */
export function trackMutations(win: Window & typeof globalThis): void {
  const marked = win as unknown as { __brunelMutationTracking?: true };
  if (marked.__brunelMutationTracking) return;
  marked.__brunelMutationTracking = true;
  const noteChangedControl = (event: Event): void => {
    const control = event.target;
    if (control instanceof Element
        && control.matches('select, input, textarea, [contenteditable]')
        && control.closest('[data-reload-preserved]') === null) changedControls.add(control);
  };
  win.addEventListener?.('input', noteChangedControl, { capture: true });
  win.addEventListener?.('change', noteChangedControl, { capture: true });
  const original = win.fetch.bind(win);
  win.fetch = (async (input: RequestInfo | URL, init?: RequestInit) => {
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const counted = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    if (counted) mutationsInFlight += 1;
    try {
      return await original(input, init);
    } finally {
      if (counted) mutationsInFlight -= 1;
    }
  }) as typeof fetch;
}

const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'number', 'password', 'date', 'time', 'datetime-local']);

function fieldLabel(field: Element): string {
  const label = field.getAttribute('aria-label') ?? field.getAttribute('placeholder') ?? field.getAttribute('name');
  return label === null || label.trim() === '' ? 'a form field' : `“${label.trim().slice(0, 60)}”`;
}

function editable(element: Element | null): boolean {
  if (element === null) return false;
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled;
  if (element instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has(element.type) && !element.readOnly && !element.disabled;
  }
  return element instanceof HTMLElement && element.isContentEditable;
}

/**
 * Text on the page that no preserver owns. A surface whose text is already
 * saved elsewhere (server-side notes) or carried by a preserver marks its
 * container `data-reload-preserved`.
 */
function unpreservedText(doc: Document): ReloadBlock | null {
  for (const field of doc.querySelectorAll('textarea, input, [contenteditable]')) {
    if (!editable(field) || field.closest('[data-reload-preserved], [data-version-popup], [hidden]') !== null) continue;
    const text = field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement
      ? field.value
      : field.textContent ?? '';
    if (text !== '') return { reason: `Unsaved text in ${fieldLabel(field)}`, preserved: false };
  }
  return null;
}

export function reloadBlocks(doc: Document, input: { readonly now: number; readonly lastActivityAt: number }): ReloadBlock[] {
  const blocks: ReloadBlock[] = [];
  if (mutationsInFlight > 0) blocks.push({ reason: 'A request is still being sent', preserved: false });
  for (const check of checks) {
    try {
      const block = check();
      if (block !== null) blocks.push(block);
    } catch {
      blocks.push({ reason: 'A page section could not report its state', preserved: false });
    }
  }
  const text = unpreservedText(doc);
  if (text !== null) blocks.push(text);
  for (const control of changedControls) {
    if (!control.isConnected) { changedControls.delete(control); continue; }
    if (control.closest('[data-reload-preserved]') !== null) continue;
    blocks.push({ reason: 'A form setting may be unsaved', preserved: false });
    break;
  }
  if (doc.querySelector('button.mic.live, .live-voice-primary.is-active') !== null) {
    blocks.push({ reason: 'Voice input is on', preserved: false });
  }
  if (doc.querySelector('dialog[open], [aria-modal="true"], [role="alertdialog"]') !== null) {
    blocks.push({ reason: 'A dialog is open', preserved: false });
  }
  const active = doc.activeElement;
  if (editable(active) && active?.closest('[data-version-popup]') === null) {
    blocks.push({ reason: 'You are typing', preserved: true });
  }
  if (input.now - input.lastActivityAt < UPDATE_QUIET_MS) {
    blocks.push({ reason: 'You are using the page', preserved: true });
  }
  return blocks;
}

interface Attempt { readonly target: string; readonly at: number }

function readAttempt(storage: Storage | null): Attempt | null {
  try {
    const parsed = JSON.parse(storage?.getItem(ATTEMPT_KEY) ?? 'null') as Partial<Attempt> | null;
    return parsed !== null && typeof parsed.target === 'string' && typeof parsed.at === 'number'
      ? { target: parsed.target, at: parsed.at }
      : null;
  } catch {
    return null;
  }
}

export interface SafeUpdateEnv {
  readonly doc: Document;
  readonly buildId: string;
  readonly storage: Storage | null;
  readonly now: () => number;
  readonly reload: () => void;
}

export interface SafeUpdate {
  /** The deployed build id as just read; the page's own id means nothing is waiting. */
  readonly deployed: (id: string) => void;
  /** Called once a second while an update is waiting. */
  readonly tick: () => void;
  readonly noteActivity: () => void;
}

export function createSafeUpdate(env: SafeUpdateEnv): SafeUpdate {
  const { doc, buildId, storage } = env;
  let target: string | null = null;
  let countdown: number | null = null;
  let postponedUntil = 0;
  let armed = false;
  let reloading = false;
  let automaticUnavailable = false;
  let lastActivityAt = env.now();

  // A reload that came back on the old build (stale edge cache) must not spin.
  const attempt = readAttempt(storage);
  let suppressedTarget: string | null = null;
  if (attempt !== null) {
    if (attempt.target === buildId || env.now() - attempt.at >= UPDATE_LOOP_WINDOW_MS) {
      try { storage?.removeItem(ATTEMPT_KEY); } catch { /* storage refused */ }
    } else {
      suppressedTarget = attempt.target;
    }
  }

  let popup: HTMLElement | null = null;
  let status: HTMLElement | null = null;
  let reloadButton: HTMLButtonElement | null = null;

  const reload = (): void => {
    if (reloading || target === null) return;
    if (!runReloadPreservers()) {
      render('A draft could not be saved for the reload. Keep this page open.', 'Reload now');
      return;
    }
    let recorded = false;
    try {
      if (storage !== null) {
        storage.setItem(ATTEMPT_KEY, JSON.stringify({ target, at: env.now() }));
        recorded = true;
      }
    } catch { /* a manual reload can still proceed after explicit owner action */ }
    if (!armed && !recorded) {
      automaticUnavailable = true;
      render('Automatic reload is unavailable because recovery storage is blocked.', 'Reload now');
      return;
    }
    reloading = true;
    env.reload();
  };

  const hide = (): void => { popup?.remove(); popup = null; };

  const show = (): void => {
    if (popup !== null) return;
    popup = doc.createElement('div');
    popup.setAttribute('data-version-popup', 'true');
    popup.setAttribute('role', 'status');
    popup.className = 'version-popup';
    const text = doc.createElement('div');
    text.className = 'version-popup-text';
    const title = doc.createElement('strong');
    title.textContent = 'Update ready — ';
    status = doc.createElement('span');
    status.setAttribute('data-version-status', 'true');
    text.append(title, status);
    reloadButton = doc.createElement('button');
    reloadButton.type = 'button';
    reloadButton.className = 'primary';
    reloadButton.addEventListener('click', () => {
      armed = true;
      tick();
    });
    const postpone = doc.createElement('button');
    postpone.type = 'button';
    postpone.textContent = 'Postpone';
    postpone.addEventListener('click', () => {
      postponedUntil = env.now() + UPDATE_POSTPONE_MS;
      armed = false;
      countdown = null;
      hide();
    });
    popup.append(text, reloadButton, postpone);
    doc.body.appendChild(popup);
  };

  const render = (message: string, button: string): void => {
    show();
    if (status !== null) status.textContent = message;
    if (reloadButton !== null) reloadButton.textContent = button;
  };

  const tick = (): void => {
    if (target === null || reloading) return;
    const now = env.now();
    if (!armed && now < postponedUntil) { hide(); return; }
    const blocks = reloadBlocks(doc, { now, lastActivityAt });
    if (armed) {
      const waiting = blocks.filter((block) => !block.preserved);
      if (waiting.length === 0) { reload(); return; }
      render(`Reloading once this clears: ${waiting[0]!.reason}.`, 'Reload now');
      return;
    }
    if (automaticUnavailable) {
      render('Automatic reload is unavailable because recovery storage is blocked.', 'Reload now');
      return;
    }
    if (suppressedTarget === target) {
      countdown = null;
      render('The last reload did not load it, so Brunel will not reload by itself.', 'Reload now');
      return;
    }
    if (blocks.length > 0) {
      countdown = null;
      render(`Brunel will reload when you are idle. Waiting: ${blocks[0]!.reason}.`, 'Reload now');
      return;
    }
    countdown = countdown === null ? UPDATE_COUNTDOWN_S : countdown - 1;
    if (countdown <= 0) { reload(); return; }
    render(`reloading in ${countdown} ${countdown === 1 ? 'second' : 'seconds'}`, 'Reload now');
  };

  return {
    deployed: (id) => {
      const next = id === buildId ? null : id;
      if (next === target) return;
      target = next;
      countdown = null;
      armed = false;
      automaticUnavailable = false;
      if (target === null) hide();
      else tick();
    },
    tick,
    noteActivity: () => {
      lastActivityAt = env.now();
      countdown = null;
    },
  };
}
